import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnInit,
    input,
    ViewEncapsulation,
    Signal,
    effect,
    ViewChild,
    signal,
} from '@angular/core';
import { FiQuizService } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz.service';
import { FiQuizHelperService } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-helper.service';
import { FiQuizEditorStoreService } from './fi-quiz-editor-store.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiQuizEditorTabIndex } from '../fi-quiz-enums';
import { IQuizEditItem } from '../fi-quiz';
import { FiQuizSettingsTabComponent } from './quiz-settings-tab/quiz-settings-tab.component';

@Component({
    selector: 'fi-quiz-edit',
    templateUrl: './fi-quiz-edit.component.html',
    styleUrl: './fi-quiz-edit.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-quiz-edit' },
    standalone: false,
})
export class FiQuizEditComponent implements OnInit {
    protected fiQuizEditorStoreService = inject(FiQuizEditorStoreService);
    private fiQuizService = inject(FiQuizService);
    protected fiQuizHelperService = inject(FiQuizHelperService);
    private saveChangesWarningService = inject(SaveChangesWarningService);
    private fiStateService = inject(FiStateService);

    protected isCopyMode = input<boolean>();

    @ViewChild(FiQuizSettingsTabComponent)
    private quizSettingsTabComponent: FiQuizSettingsTabComponent;

    protected quiz: Signal<IQuizEditItem> = this.fiQuizEditorStoreService.currentQuizData;
    protected quizHeaderText = computed(() => {
        return this.fiQuizHelperService.getQuizHeaderText(this.quiz()?.Name, this.isCopyMode());
    });
    protected selectedTabIndex: Signal<FiQuizEditorTabIndex> = this.fiQuizEditorStoreService.selectedTabIndex;

    private _quizSharingSettingsSaved: boolean = false;
    private _isQuizSharingSettingsSavedEffect = effect(() => {
        const isQuizSharingSettingsSaved = this.fiQuizEditorStoreService.isQuizSharingSettingsSaved();
        this._quizSharingSettingsSaved = isQuizSharingSettingsSaved;
        if (this._quizSharingSettingsSaved) {
            this.save();
        }
    });

    public ngOnInit(): void {
        this.startWatchingChanges();
    }

    private startWatchingChanges(): void {
        this.saveChangesWarningService.start((isReceivingInitialObject) =>
            isReceivingInitialObject ? this.fiQuizEditorStoreService.initialQuizData() : this.quiz(),
        );
    }

    protected onFormValidateSuccess(): void {
        if (!this.quizSettingsTabComponent || this._quizSharingSettingsSaved) {
            this.save();
            return;
        }
        this.fiQuizEditorStoreService.triggerQuizSharingSettingsSave();
    }

    private save(): void {
        this.fiQuizService.saveQuiz(this.quiz()).then(() => {
            this.saveChangesWarningService.stop();
            this.fiStateService.go(FiStateNames.Onboarding.Quiz.List);
        })
    }
}
